import { HYDRATION_REWRITE_MONITOR_SCRIPT } from './hydration-rewrite-monitor';
import type { SsrTrace } from './ssr-trace';

export interface BootstrapRuntimeEnv {
	readonly cdnUrl: string;
	readonly imgproxyUrl: string;
	readonly analyticsCode: string;
	readonly recaptchaSiteKey: string;
	readonly adyenClientKey: string;
	readonly usercentricsSettingsId: string;
	/** `'true'` on every stage but `pro`; see the `data-draft` note in `loadUsercentrics`. */
	readonly usercentricsDraft: string;
}

/**
 * Always both, in this order, an empty `href` rather than a conditional: a client without the stash rendered two
 * fewer head children and React threw the tree away (TEC-3825). docs/notes/portals/head-bootstrap.md
 */
export function headPreconnects(
	env: Pick<BootstrapRuntimeEnv, 'cdnUrl' | 'imgproxyUrl'>,
): readonly { readonly key: string; readonly href: string }[] {
	return [
		{ key: 'cdn', href: env.cdnUrl },
		{ key: 'imgproxy', href: env.imgproxyUrl },
	];
}

/** Emitted with empty ids when there is no trace, so the client's rebuilt text is byte-identical either way. */
function initTraceScript(trace: SsrTrace | undefined): string {
	return (
		`window.__TRACE_ID=${JSON.stringify(trace?.traceId ?? '')};` +
		`window.__ROOT_SPAN_ID=${JSON.stringify(trace?.rootSpanId ?? '')};` +
		// Lets the browser tell it is hydrating a document rendered for another URL (TEC-3854); per-request like the ids.
		`window.__RENDER_URL=${JSON.stringify(trace?.url ?? '')};`
	);
}

/**
 * Byte-identical on server and client, or TanStack's `Script` dedup fails and re-runs the whole bootstrap on
 * every hydration; hence the per-request trace ids live in their own script.
 */
function initAssetUrlScript(env: BootstrapRuntimeEnv): string {
	return (
		`window.__CDN_URL=${JSON.stringify(env.cdnUrl)};` +
		`window.__IMGPROXY_URL=${JSON.stringify(env.imgproxyUrl)};` +
		`window.__ANALYTICS_CODE=${JSON.stringify(env.analyticsCode)};` +
		`window.__GOOGLE_RECAPTCHA_SITE_KEY=${JSON.stringify(env.recaptchaSiteKey)};` +
		`window.__ADYEN_CLIENT_KEY=${JSON.stringify(env.adyenClientKey)};` +
		`window.__USERCENTRICS_SETTINGS_ID=${JSON.stringify(env.usercentricsSettingsId)};` +
		`window.__USERCENTRICS_DRAFT=${JSON.stringify(env.usercentricsDraft)};` +
		`globalThis.__assetUrl=function(f){var b=window.__CDN_URL||'';return (b?b.replace(/\\/$/,'')+'/':'/')+f}`
	);
}

/**
 * Consent Mode v2 defaults, all denied: required of the website, and only the backstop to `loadGtag`'s consent
 * gate, which the external privacy audit demanded. docs/notes/portals/head-bootstrap.md
 */
function initConsentModeScript(): string {
	return (
		'window.dataLayer=window.dataLayer||[];' +
		'window.gtag=window.gtag||function(){window.dataLayer.push(arguments)};' +
		'window.gtag("consent","default",{' +
		'ad_storage:"denied",' +
		'ad_user_data:"denied",' +
		'ad_personalization:"denied",' +
		'analytics_storage:"denied",' +
		// A backstop for embeds only; it never held an early event (the audit saw a `collect` at ~3.9s, banner open).
		'wait_for_update:3000' +
		'});'
	);
}

/**
 * Every entry stays inline and unconditional: a `src` script with no children gets React's leftover-children
 * check, and a head injection then throws `#418 args[]=HTML` (DR-24). The rewrite recorder stays last, ahead of
 * the async entry module. docs/notes/portals/head-bootstrap.md
 */
export function bootstrapHeadScripts(env: BootstrapRuntimeEnv, trace: SsrTrace | undefined): readonly string[] {
	return [initTraceScript(trace), initAssetUrlScript(env), initConsentModeScript(), HYDRATION_REWRITE_MONITOR_SCRIPT];
}
